import React, { useState, useEffect, useRef } from "react";
import MasterTable from "../../../components/common/MasterTable";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import CustomAlert from "../../../helpers/CustomAlert";
import Loader from "../../../components/Loader/Loader";
import HeaderWithBreadcrumb from "../../../components/common/HeaderWithBreadcrumb";
import BuyStationaryModal from "./BuyStationaryModal";
import { useDashboardData } from "../../../Hooks/useDashboardData";
import { useSelector } from "react-redux";
import { formatAmount } from "../../../helpers/helper";
import { format, parse } from "date-fns";
import DownloadReport from "../../../components/transactionReport/DownloadReport";
import BuyStationaryFilterModal from "./BuyStationaryFilterModal";
import FilterBar from "../../../components/common/FilterBar";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";

const REQUEST_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

// Helper function to get date 7 days ago in YYYY-MM-DD format
const getLast7DaysDate = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);

  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const defaultFilterData = {
  itemCode: "",
  itemName: "",
  createdBy: "",
  isActive: null,
  fromDate: getLast7DaysDate(),
  toDate: getTodayDate(),
  status: "",
  search: "",
};

const BuyStationaryMaster = () => {
  const loggedInUserName =
    sessionStorage.getItem("username") ||
    sessionStorage.getItem("userId") ||
    "";
  const { fetchDashboardData } = useDashboardData();
  const { user } = useSelector((state) => state.auth);
  const [pageSize, setPageSize] = useState(10);
  const hasFetchedDashboard = useRef(false);

  const [noData, setNoData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [vendorData, setVendorData] = useState([]);
  const [modalAction, setModalAction] = useState("");
  const [pageDetails, setPageDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [columnData, setColumnData] = useState("");
  const [isOpenDownloadReportModal, setIsOpenDownloadReportModal] =
    useState(false);
  const [pageDowRepDetails, setPageDowRepDetails] = useState(null);
  const [noDowRepData, setNoDowRepData] = useState(false);
  const [loadingDowRep, setLoadingDowRep] = useState(false);
  const [stickerMasterData, setStickerMasterData] = useState([]);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [remarkText, setRemarkText] = useState("");
  const [remarkLoading, setRemarkLoading] = useState(false);

  const openModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const openModalReportDown = () => {
    setIsOpenDownloadReportModal(true);
  };

  const closeModalReportDown = () => {
    setIsOpenDownloadReportModal(false);
  };

  const handleReset = () => {
    const resetData = {
      itemName: "",
      createdBy: "",
      isActive: null,
      fromDate: getLast7DaysDate(),
      toDate: getTodayDate(),
      status: "",
      search: "",
    };
    setFilterData(resetData);
    fetchBuyStationaryData(1, resetData, pageSize);
  };

  useEffect(() => {
    fetchBuyStationaryData(1, filterData, pageSize);
  }, []);

  // Helper function to format date from YYYY-MM-DD to DD-MM-YYYY
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const fetchBuyStationaryData = (
    currentPage,
    filters = filterData,
    size = pageSize,
  ) => {
    const payload = {
      username: loggedInUserName,
    };

    // Add filters to payload only if they have values
    if (filters.itemName && filters.itemName.trim() !== "") {
      payload.itemName = filters.itemName.trim();
    }

    if (filters.createdBy && filters.createdBy.trim() !== "") {
      const trimmedCreatedBy = filters.createdBy.trim();
      const idPart = trimmedCreatedBy.includes(" - ")
        ? trimmedCreatedBy.split(" - ")[0].trim()
        : trimmedCreatedBy;
      const namePart = trimmedCreatedBy.includes(" - ")
        ? trimmedCreatedBy.split(" - ")[1].trim()
        : trimmedCreatedBy;

      payload.createdBy = idPart;
      payload.lmbpUsername = idPart;
      payload.lmbpName = namePart;
    }

    if (filters.isActive !== null && filters.isActive !== "") {
      payload.isActive = filters.isActive;
    }

    if (filters.fromDate && filters.fromDate.trim() !== "") {
      payload.fromDate = formatDateForAPI(filters.fromDate);
    }

    if (filters.toDate && filters.toDate.trim() !== "") {
      payload.toDate = formatDateForAPI(filters.toDate);
    }

    if (filters.status && filters.status.trim() !== "") {
      payload.status = filters.status;
    }

    // item name / code, LMBP username or name (matched on the server)
    if (filters.search && filters.search.trim() !== "") {
      payload.search = filters.search.trim();
    }

    setLoading(true);

    axiosPostApi(
      `${apiList.FETCH_BUY_STATIONARY_MASTER}?&pageSize=${size}&pageNo=${currentPage}`,
      payload,
    )
      .then((response) => {
        if (
          response?.data?.status === true &&
          response?.data?.respCode === "00"
        ) {
          setPageDetails(response?.data?.pageDetails);
          let modifiedData = modifyStructure(response?.data?.data);

          // If filtering by createdBy, ensure matching even if backend doesn't filter
          if (filters.createdBy && filters.createdBy.trim() !== "") {
            const rawQuery = filters.createdBy.trim().toLowerCase();
            const idPart = (
              filters.createdBy.includes(" - ")
                ? filters.createdBy.split(" - ")[0].trim()
                : filters.createdBy.trim()
            ).toLowerCase();
            const namePart = (
              filters.createdBy.includes(" - ")
                ? filters.createdBy.split(" - ")[1].trim()
                : filters.createdBy.trim()
            ).toLowerCase();

            modifiedData = modifiedData.filter((item) => {
              const usernameStr = (item?.lmbpUsername || "").toLowerCase();
              const nameStr = (item?.lmbpName || "").toLowerCase();
              const createdByStr = (item?.createdBy || "").toLowerCase();
              return (
                usernameStr.includes(rawQuery) ||
                usernameStr.includes(idPart) ||
                usernameStr.includes(namePart) ||
                nameStr.includes(rawQuery) ||
                nameStr.includes(namePart) ||
                createdByStr.includes(rawQuery) ||
                createdByStr.includes(idPart)
              );
            });
          }

          setVendorData(modifiedData);

          if (!hasFetchedDashboard.current) {
            fetchDashboardData();
            hasFetchedDashboard.current = true;
          }

          if (modifiedData?.length === 0) {
            setNoData(true);
          } else {
            setNoData(false);
          }
        } else {
          setNoData(true);
          setVendorData([]);
          // if (response?.data?.message) {
          //   CustomAlert({
          //     title: "No Data Found",
          //     text: response?.data?.message || "No matching records found.",
          //     icon: "info",
          //     confirmButtonText: "Okay",
          //   });
          // }
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        setNoData(true);
        setVendorData([]);
        CustomAlert({
          title: "Error",
          text: "An error occurred while fetching the data. Please try again later.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const modifyStructure = (response) => {
    if (!response || !Array.isArray(response)) return [];

    return response?.map((item, index) => ({
      ...item,
      id: item.id || index + 1,
      isActive: item.isActive ? "Active" : "Inactive",
      // Who raised the request: person's name and their user ID
      requestedBy: [item?.lmbpName, item?.lmbpUsername ? `(${item.lmbpUsername})` : ""]
        .filter(Boolean)
        .join(" "),
      remarks:
        item?.remarks &&
        item?.remarks.trim() !== "" &&
        item?.remarks.trim() !== "-"
          ? item.remarks.trim()
          : "",

      lmbpCustomerName: `${item?.lmbpUserCode || item?.lmbpCode || ""} - ${
        item?.lmbpCustomerName || ""
      }`,
      //for the name of user
      lmbpUsername: `${item?.lmbpUsername || ""}${
          item?.lmbpName ? ` - ${item.lmbpName}` : ""
      }`,

      approvedHubName: `${item?.approvedBy || ""} - ${
        item?.approvedHubName || ""
      }`,

      requestedAt: item?.requestedAt
        ? [
            format(
              parse(item?.requestedAt, "dd-MM-yyyy HH:mm:ss", new Date()),
              "dd-MM-yyyy",
            ),
            <br key={`requested-${index}`} />,
            format(
              parse(item?.requestedAt, "dd-MM-yyyy HH:mm:ss", new Date()),
              "HH:mm:ss",
            ),
          ]
        : "",

      approvedAt: item?.approvedAt
        ? [
            format(
              parse(item?.approvedAt, "dd-MM-yyyy HH:mm:ss", new Date()),
              "dd-MM-yyyy",
            ),
            <br key={`approved-${index}`} />,
            format(
              parse(item?.approvedAt, "dd-MM-yyyy HH:mm:ss", new Date()),
              "HH:mm:ss",
            ),
          ]
        : "",

      unitRate: (
        <div
          style={{
            textAlign: "end",
            width: "80px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.unitRate)}
        </div>
      ),

      quantity: (
        <div
          style={{
            textAlign: "end",
            width: "100px",
            whiteSpace: "nowrap",
          }}
        >
          {item?.quantity}
        </div>
      ),

      baseAmount: (
        <div
          style={{
            textAlign: "end",
            width: "80px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.baseAmount)}
        </div>
      ),

      customerOldBalance: (
        <div
          style={{
            textAlign: "end",
            width: "80px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.customerOldBalance)}
        </div>
      ),

      totalAmount: (
        <div
          style={{
            textAlign: "end",
            width: "80px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.totalAmount)}
        </div>
      ),

      customerNewBalance: (
        <div
          style={{
            textAlign: "end",
            width: "100px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.customerNewBalance)}
        </div>
      ),

      gstAmount: (
        <div
          style={{
            textAlign: "end",
            width: "80px",
            whiteSpace: "nowrap",
          }}
        >
          {formatAmount(item?.gstAmount)}
        </div>
      ),

      quantityE: item?.quantity,
      unitRateE: item?.unitRate,
    }));
  };

  const tableHeaderToShow = user?.data?.userProfile === "HUB" ||
    user?.data?.userProfile === "OPERATION"

  const stationaryHeaders = [
    "Status",
    "Remarks",
    "Stationary Code",
    "Stationary Name",
    "Created Date",
    "Requested By",
    ...(tableHeaderToShow
      ? ["LMBP Name"] // HUB
      : []),
    "Authorize Date",
    ...(tableHeaderToShow
      ? ["Authorized By"] // HUB
      : []),

    "Unit Rate (₹)",
    "GST %",
    "Quantity (Qty)",

    "Excl. GST (₹)",
    "GST Amt (₹)",
    "Incl. GST (₹)",
    ...(tableHeaderToShow
      ? ["Latest Bal (₹)", "Old Bal (₹)"] // HUB
      : []),
  ];

  const stationaryHeaderToKeyMap = {
    Status: "status",
    Remarks: "remarks",
    "Stationary Code": "itemCode",
    "Stationary Name": "itemName",
    "Created Date": "requestedAt",

    "Requested By": "requestedBy",
    "LMBP Name": "lmbpCustomerName",

    "Authorize Date": "approvedAt",

    "Authorized By": "approvedHubName",

    "Unit Rate (₹)": "unitRate",
    "GST %": "gstPercent",
    "Quantity (Qty)": "quantity",

    "Excl. GST (₹)": "baseAmount",
    "GST Amt (₹)": "gstAmount",
    "Incl. GST (₹)": "totalAmount",

    "Latest Bal (₹)": "customerNewBalance",
    "Old Bal (₹)": "customerOldBalance",
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData({});
    setModalAction("");
  };

  const updateStationaryRequest = async (status) => {
    setLoading(true);
    try {
      const payload = {
        username: (loggedInUserName || "").trim().toUpperCase(),
        requestId: columnData?.id || editData?.id,
        status: status,
      };

      const api = apiList.MODIFY_BUY_STATIONARY_MASTER;
      const response = await axiosPostApi(api, payload);

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        const data = response?.data;
        CustomAlert({
          title: "Success",
          text: data?.message || "Request updated successfully",
          icon: "success",
        }).then(() => {
          fetchDashboardData();
          fetchBuyStationaryData(currentPage, filterData, pageSize);
        });
        setShowModal(false);
      } else {
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "Failed to fetch data",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemarkClick = (row) => {
    setSelectedRow(row);
    const existingRemark =
      row?.remarks && row?.remarks.trim() !== "-" ? row.remarks.trim() : "";
    setRemarkText(existingRemark);
    setRemarkModalOpen(true);
  };

  const handleRemarkSubmit = async () => {
    if (!remarkText.trim()) {
      CustomAlert({
        title: "Warning",
        text: "Please enter a remark",
        icon: "warning",
      });
      return;
    }

    try {
      setRemarkLoading(true);
      const payload = {
        requestId: selectedRow?.id,
        remarks: remarkText.trim(),
        username: (loggedInUserName || "").trim().toUpperCase(),
      };

      const response = await axiosPostApi(
        apiList.ADD_REMARK_BUY_STATIONARY,
        payload,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        CustomAlert({
          title: "Success",
          text: response?.data?.message || "Remark saved successfully",
          icon: "success",
        }).then(() => {
          setRemarkModalOpen(false);
          setSelectedRow(null);
          setRemarkText("");
          fetchBuyStationaryData(currentPage, filterData, pageSize);
        });
      } else {
        CustomAlert({
          title: "Failed",
          text: response?.data?.message || "Failed to save remark",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while saving the remark. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setRemarkLoading(false);
    }
  };

  const handlerBuyStationary = (data, actionType = "view") => {
    if (actionType === "remark") {
      handleRemarkClick(data);
      return;
    }

    setColumnData(data);
    setModalAction(actionType);

    if (actionType === "add") {
      setEditData({});
      setShowModal(true);
    } else if (actionType === "approve/reject") {
      let venData = vendorData.find((item) => item?.id === data?.id);
      if (venData) {
        setEditData(venData);
        setShowModal(true);
      }
    } else if (actionType === "generateReport") {
      openModalReportDown();
    } else {
      let venData = vendorData.find((item) => item?.id === data?.id);
      if (venData) {
        setEditData(venData);
        setShowModal(true);
      }
    }
  };

  // Fetch Data For Generate Sticker
  const fetchGenerateStickerData = async (currentPage, size = pageSize) => {
    const payload = {
      username: loggedInUserName,
    };

    setLoadingDowRep(true);

    try {
      const response = await axiosPostApi(
        `${apiList.FETCH_GENERATE_STICKER}?&pageSize=${size}&pageNo=${currentPage}`,
        payload,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        const data = response?.data?.data;
        setPageDowRepDetails(response?.data?.pageDetails);
        setStickerMasterData(data);
        setNoDowRepData(data?.length === 0);
      } else {
        setStickerMasterData([]);
        setNoDowRepData(true);
      }
    } catch (error) {
      console.warn("⚠️ API failed - showing dummy data for UI testing");
      setStickerMasterData([]);
      setNoDowRepData(true);
    } finally {
      setLoadingDowRep(false);
    }
  };

  const generateStickerHeader = [
    "Quantity",
    "City",
    "Requested by",
    "Mobile No",
    "Barcode series",
    "Status",
    "Approved by",
    "Remark",
    "Download",
  ];

  const generateStickerHeaderToKeyMap = {
    Quantity: "quantity",
    City: "city",
    "Requested by": "requestedBy",
    "Mobile No": "mobile",
    "Barcode series": "barcodeSeries",
    Status: "status",
    "Approved by": "approvedBy",
    Remark: "remarks",
    Download: "fileUrl",
  };

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <div>
      <HeaderWithBreadcrumb title="Buy Stationary" breadcrumb="Master" />
      <FilterBar
        values={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchBuyStationaryData(1, next, pageSize);
        }}
        search={{
          key: "search",
          placeholder: "Search item name / code or LMBP",
        }}
        selects={[
          { key: "status", allLabel: "All statuses", chipLabel: "Status", options: REQUEST_STATUS_OPTIONS },
        ]}
        dateRange={{ fromKey: "fromDate", toKey: "toDate", label: "Requested" }}
      />
      <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
        {loading ? (
          <Loader />
        ) : (
          <>
            <MasterTable
              headers={stationaryHeaders}
              data={vendorData}
              handleAction={handlerBuyStationary}
              filterFields={["itemName"]}
              entityType="BuyStationary"
              headerToKeyMap={stationaryHeaderToKeyMap}
              loading={loading}
              noData={noData}
              pageDetails={pageDetails}
              currentPage={currentPage}
              pageSize={pageSizeCount}
              totalRecord={totalRecord}
              fetchNextData={(page, size) =>
                fetchBuyStationaryData(page, filterData, size)
              }
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              fetchData={() =>
                fetchBuyStationaryData(1, defaultFilterData, pageSize)
              }
              hideFilterControls
            />
            {showModal && (
              <BuyStationaryModal
                show={showModal}
                handleClose={handleCloseModal}
                editData={editData}
                action={modalAction}
                callApi={() =>
                  fetchBuyStationaryData(currentPage, filterData, pageSize)
                }
                handleAction={updateStationaryRequest}
              />
            )}

            {/* Generate Sticker Download Modal */}
            {isOpenDownloadReportModal && (
              <DownloadReport
                handleopenReportDown={isOpenDownloadReportModal}
                handleCloseReportDown={closeModalReportDown}
                callApi={fetchGenerateStickerData}
                data={stickerMasterData}
                pageDowRepDetails={pageDowRepDetails}
                noData={noDowRepData}
                loading={loadingDowRep}
                header={generateStickerHeader}
                headerToKey={generateStickerHeaderToKeyMap}
                entityType="GenerateSticker"
              />
            )}

            {/* Filter Modal */}
            {isModalOpenFilter && (
              <BuyStationaryFilterModal
                show={isModalOpenFilter}
                handleClose={closeModalFilter}
                filterData={filterData}
                setFilterData={setFilterData}
                handleReset={handleReset}
                setCurrentPage={setCurrentPage}
                fetchData={(filters) =>
                  fetchBuyStationaryData(1, filters, pageSize)
                }
                dataList={vendorData}
              />
            )}

            {/* 💬 Remark Modal */}
            <Dialog
              open={remarkModalOpen}
              onClose={() => !remarkLoading && setRemarkModalOpen(false)}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                style: { borderRadius: "16px", padding: "8px" },
              }}
            >
              <DialogTitle sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                {selectedRow?.remarks &&
                selectedRow?.remarks.trim() !== "" &&
                selectedRow?.remarks.trim() !== "-"
                  ? "Update Remark"
                  : "Add Remark"}
                <span
                  style={{
                    fontSize: "0.82rem",
                    color: "#666",
                    display: "block",
                    fontWeight: 400,
                    marginTop: "4px",
                  }}
                >
                  Request #{selectedRow?.id} &mdash; {selectedRow?.itemName}
                </span>
              </DialogTitle>
              <DialogContent dividers>
                <TextField
                  autoFocus
                  multiline
                  minRows={3}
                  maxRows={6}
                  fullWidth
                  value={remarkText}
                  onChange={(e) => setRemarkText(e.target.value)}
                  placeholder="Enter remark..."
                  disabled={remarkLoading}
                  variant="outlined"
                  size="small"
                />
              </DialogContent>
              <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button
                  onClick={() => setRemarkModalOpen(false)}
                  disabled={remarkLoading}
                  variant="outlined"
                  color="inherit"
                  sx={{ borderRadius: "8px", textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRemarkSubmit}
                  disabled={remarkLoading}
                  sx={{
                    backgroundColor: "#ff7722",
                    "&:hover": { backgroundColor: "#e06516" },
                    borderRadius: "8px",
                    textTransform: "none",
                    minWidth: "90px",
                  }}
                >
                  {remarkLoading ? "Saving..." : "Submit"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </div>
    </div>
  );
};

export default BuyStationaryMaster;
